import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Lock, CheckCircle2, Minus, RefreshCw } from "lucide-react";
import { fade, Eyebrow, PageHeader, usePageMeta } from "@/components/SiteLayout";
import { WorkflowStrip } from "@/components/WorkflowStrip";

const REPORT_SECTIONS = [
  { n: 1, title: "The Verdict", desc: "Where your asset stands, the recommendation, and the conditions attached — distilled for a governance decision." },
  { n: 2, title: "The Risks", desc: "What assessors are likely to challenge — prioritized, quantified, and each paired with a mitigation." },
  { n: 3, title: "The Plan", desc: "The sequenced evidence roadmap, what each investment buys, and the actions your board must own." },
];

const SCOPE_TIERS = [
  { name: "JCA Evidence Stress Test", q: "What will assessors challenge first?", scale: "2–4 weeks · Single asset", tagline: "The fast, focused risk diagnostic." },
  { name: "JCA Readiness Assessment", q: "If Article 9 started tomorrow — would we pass?", scale: "4–8 weeks · One indication", tagline: "The full board-ready verdict.", recommended: true },
  { name: "JCA Evidence Investment Strategy", q: "Where should we invest next — and what can wait?", scale: "3–6 months · Multi-PICO", tagline: "The verdict, extended across every population and market that matters." },
  { name: "JCA Evidence Monitoring", q: "Has our risk changed since last quarter?", scale: "Quarterly · Per asset", tagline: "The verdict, kept alive — reassessed every quarter." },
];

const SCOPE_ROWS = [
  { dim: "Executive dashboard & verdict", by: ["included", "included", "included", "continuous"] },
  { dim: "Strategic risk register", by: ["included", "included", "expanded", "continuous"] },
  { dim: "Full evidence assessment", by: ["", "included", "expanded", "continuous"] },
  { dim: "Country & national HTA outlook", by: ["", "included", "expanded", "continuous"] },
  { dim: "Evidence strategy roadmap", by: ["", "included", "expanded", "continuous"] },
  { dim: "Continuous monitoring & re-issue", by: ["", "", "", "continuous"] },
];

const STATUS_META = {
  included: { chip: "bg-blue-50 text-blue-700 border-blue-200/60", label: "Included" },
  expanded: { chip: "bg-indigo-50 text-indigo-700 border-indigo-200/60", label: "Expanded" },
  continuous: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200/60", label: "Continuous" },
};

const ScopePicker = () => {
  const [sel, setSel] = useState(1);
  const tier = SCOPE_TIERS[sel];
  return (
    <div className="mt-10 grid lg:grid-cols-12 gap-6 items-stretch" data-testid="scope-picker">
      <div className="lg:col-span-5 space-y-3">
        {SCOPE_TIERS.map((t, i) => (
          <button key={t.name} onClick={() => setSel(i)} data-testid={`scope-tier-btn-${i}`}
            className={`w-full text-left rounded-2xl border px-6 py-5 transition-all duration-300 ${sel === i ? "border-2 border-blue-600 bg-white shadow-[0_16px_40px_-20px_rgba(37,99,235,0.35)]" : "border-slate-200 bg-white hover:border-blue-300 hover:-translate-y-0.5"}`}>
            <div className="flex items-center justify-between gap-3">
              <span className={`font-display text-base font-semibold italic leading-snug ${sel === i ? "text-blue-700" : "text-slate-800"}`}>“{t.q}”</span>
              {t.recommended && <span className="rounded-full bg-blue-600 text-white px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap shrink-0">★ Popular</span>}
            </div>
            <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{t.name}</span> · {t.scale}
            </div>
          </button>
        ))}
      </div>
      <div className="lg:col-span-7">
        <AnimatePresence mode="wait">
          <motion.div key={sel} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="h-full rounded-2xl border border-slate-200 bg-slate-900 p-7 flex flex-col" data-testid="scope-panel">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-blue-400">How far the answer reaches</div>
                <h3 className="mt-1.5 font-display text-xl font-semibold text-white">{tier.name}</h3>
                <p className="mt-1 text-sm text-slate-400">{tier.tagline}</p>
              </div>
              <div className="flex gap-1 shrink-0 mt-1" title="Depth of scope">
                {[0, 1, 2, 3].map((n) => <span key={n} className={`h-2 w-6 rounded-full ${n <= sel ? "bg-blue-500" : "bg-slate-700"}`} />)}
              </div>
            </div>
            <div className="mt-5 space-y-2 flex-1">
              {SCOPE_ROWS.map((r, i) => {
                const st = r.by[sel];
                const m = STATUS_META[st];
                return (
                  <div key={r.dim} className={`rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 ${st ? "bg-white/[0.06]" : "bg-transparent"}`} data-testid={`scope-dim-${i}`}>
                    <span className={`text-sm ${st ? "text-slate-100 font-medium" : "text-slate-600 line-through decoration-slate-700"}`}>{r.dim}</span>
                    {m ? (
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold shrink-0 ${m.chip}`}>
                        {st === "continuous" && <RefreshCw className="h-3 w-3 inline mr-1 -mt-px" />}{m.label}
                      </span>
                    ) : (
                      <Minus className="h-4 w-4 text-slate-700 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-4 pt-4 border-t border-white/10 text-xs text-slate-500">
              {sel === 0 && "Everything here rolls forward — a Stress Test converts into the Readiness Assessment without repeating work."}
              {sel === 1 && "The engagement most clients choose — one board-ready verdict covering every dimension of your evidence."}
              {sel === 2 && "Expanded means deeper: every population, subgroup and market — sequenced by closing evidence windows."}
              {sel === 3 && "Continuous means alive: your verdict and risk picture re-issued each quarter as data, competitors and PICOs move."}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const OUTCOMES = [
  "Evidence gaps identified months before submission",
  "Comparator strategy aligned before assessor questions arrive",
  "One prioritized evidence roadmap instead of fragmented analyses",
  "Every recommendation traceable back to supporting evidence",
  "Faster governance decisions, because risks are visible early",
];

const HIERARCHY_TOP = { title: "Regulatory documents", desc: "The highest-authority tier — sources formally reviewed by regulators." };
const HIERARCHY_BOTTOM = { title: "Internal assumptions", desc: "The lowest tier — positions without direct document support, always flagged as such." };

export default function Deliverables() {
  usePageMeta(
    "Outcomes — Board-ready JCA Decision Packages | JCA Matrix™",
    "Every MarksMan engagement produces one board-ready decision package — consultant-validated, evidence-traceable and scoped to the decision you need to make."
  );
  return (
    <>
      <PageHeader
        eyebrow="What supports your decisions"
        title="One board-ready decision package. Tailored to the decision you need to make."
        intro="Every engagement produces executive decision support — prepared and validated by senior MarksMan consultants, powered by the JCA Matrix™ JCA Readiness & Strategy Platform, and traceable to its evidence. The engagement determines the decision it supports."
      />

      {/* Scope by engagement */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>Scoped to your decision</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
            Start with the question <span className="text-blue-600">keeping you up at night</span>.
          </h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
            Every engagement runs on the same engine, with the same rigor. What changes is how far the answer
            reaches — pick your question and see exactly what the answer includes.
          </p>
          <ScopePicker />
          <p className="mt-5 text-sm text-slate-500 flex items-center gap-2" data-testid="scope-note">
            <FileText className="h-4 w-4 text-slate-400" /> Representative dimensions only — the full scope architecture is defined in your confidential proposal. Every deliverable is consultant-validated and delivered with a walkthrough — never a raw AI export.
          </p>
          <WorkflowStrip className="mt-14" />
        </div>
      </section>

      {/* Inside the report */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Eyebrow>Inside your decision package</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
            This is what your board sees.
          </h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
            One coherent narrative, built for a governance decision — and whatever the engagement, it answers the
            three questions every board asks. Every conclusion traceable to the evidence behind it.
          </p>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="report-pages-grid">
            {REPORT_SECTIONS.map((p, i) => (
              <motion.div key={p.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 6}
                className="relative rounded-xl border border-slate-200 bg-white p-6 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-300" data-testid={`report-page-card-${p.n}`}>
                <div className="absolute -top-3 -right-1 font-display text-7xl font-bold text-slate-100 select-none leading-none">{String(p.n).padStart(2, "0")}</div>
                <div className="relative">
                  <h3 className="font-display text-base font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
              className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/60 p-6 flex flex-col items-center justify-center text-center"
              data-testid="proprietary-sections-card">
              <Lock className="h-5 w-5 text-slate-400" />
              <h3 className="mt-3 font-display text-base font-semibold tracking-tight text-slate-700">The full structure is proprietary</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">The section-by-section architecture of the decision package — and the methodology behind it — is shared during scoping, under confidentiality.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What clients gain */}
      <section className="py-24 bg-slate-900" data-testid="client-outcomes">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400">Outcomes, not artifacts</div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">What clients gain</h2>
          </div>
          <div className="mt-12 space-y-4 max-w-3xl mx-auto">
            {OUTCOMES.map((t, i) => (
              <motion.div key={t} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex items-start gap-3.5 rounded-xl border border-slate-800 bg-slate-800/50 px-5 py-4"
                data-testid={`outcome-${i}`}>
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-slate-200 font-medium">{t}</span>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-center font-display text-lg font-semibold text-slate-400">Not AI. <span className="text-blue-400">Confidence.</span></p>
        </div>
      </section>

      {/* Evidence hierarchy */}
      <section className="py-24" data-testid="evidence-hierarchy">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>Where every claim comes from</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Every claim, graded by <span className="text-blue-600">the source behind it.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              A defensible verdict depends on where each claim comes from. Every finding in a MarksMan deliverable
              is graded against our proprietary 9-level source hierarchy — nothing reaches your board ungraded.
            </p>
          </div>
          <div className="mt-12 space-y-2.5 max-w-3xl mx-auto">
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5 hover:border-blue-300 transition-all duration-300"
              data-testid="hierarchy-tier-top">
              <span className="h-8 w-8 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">01</span>
              <div className="min-w-0">
                <div className="font-display text-sm font-semibold tracking-tight">{HIERARCHY_TOP.title}</div>
                <p className="text-xs text-slate-500 leading-relaxed">{HIERARCHY_TOP.desc}</p>
              </div>
            </motion.div>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
              className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/70 px-5 py-6 ml-[4%]" data-testid="hierarchy-locked">
              <div className="space-y-2 select-none" aria-hidden="true">
                {[0, 1, 2].map((n) => (
                  <div key={n} className="flex items-center gap-4 opacity-40 blur-[3px]" style={{ marginLeft: `${n * 4}%` }}>
                    <span className="h-7 w-7 rounded-lg bg-slate-400 shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-2.5 w-40 rounded bg-slate-400" />
                      <div className="h-2 w-72 max-w-full rounded bg-slate-300" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <Lock className="h-5 w-5 text-slate-400" />
                <p className="mt-2 font-display text-sm font-semibold text-slate-700">Tiers 02–08 — proprietary</p>
                <p className="mt-1 text-xs text-slate-500 max-w-sm">The ranked tiers in between — and how each is weighted — are the core of our methodology, shared with clients during engagements under confidentiality.</p>
              </div>
            </motion.div>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5 hover:border-blue-300 transition-all duration-300 ml-[8%]"
              data-testid="hierarchy-tier-bottom">
              <span className="h-8 w-8 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">09</span>
              <div className="min-w-0">
                <div className="font-display text-sm font-semibold tracking-tight">{HIERARCHY_BOTTOM.title}</div>
                <p className="text-xs text-slate-500 leading-relaxed">{HIERARCHY_BOTTOM.desc}</p>
              </div>
            </motion.div>
          </div>
          <p className="mt-8 text-center text-sm text-slate-500 max-w-2xl mx-auto">
            What you can always see: every claim in your deliverable carries its source grade — so your board knows
            exactly how much weight each conclusion can bear.
          </p>
        </div>
      </section>
    </>
  );
}
